/**
 * This custom hook manages the fetching, filtering, sorting, and paginating of work packets.
 * Note: This hook is designed to work with MUI's DataGrid component.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { type GridFilterModel, type GridRowSelectionModel, type GridSortModel } from "@mui/x-data-grid";
import {
  type WorkPacketTypeMap,
  WorkPacketFilter,
  type WorkPacketFilters,
  MyPacketOwnerView,
} from "src/types/work-packets";
import { useAuth0 } from "@auth0/auth0-react";
import { getUpdatedFields } from "./util/getUpdatedFields";
import { WorkPacketView } from "./WorkPacketView";
import * as WorkPacketsAPI from "./api/workPacketsAPI";
import toast from "react-hot-toast";
import { useWorkPacketsContext } from "./WorkPacketsContext";
import { WorkPacketType } from "./WorkPacketType";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { GridLocaleText } from "@mui/x-data-grid/models/api";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { SelectionMode, useRowSelection } from "src/hooks/useRowSelection";

interface PaginationModel {
  page: number;
  pageSize: number;
}

interface UseWorkPacketsReturnType<T extends WorkPacketType> {
  /** Grid rows */
  data: WorkPacketTypeMap[T][];
  /** Pagination model: current page and page size */
  paginationModel: PaginationModel;
  /** Total number of rows (not just the current page) */
  total: number;
  /** Loading state while fetching data */
  loading: boolean;
  /** Filter values */
  filters: WorkPacketFilters;
  /** Update filter values */
  setFilters: (filters: WorkPacketFilters) => void;
  /** Selected rows (row IDs) including all pages */
  rowSelection: GridRowSelectionModel;
  /** Keeps track of whether user clicked "Select All" */
  selectAllEnabled: boolean;
  /** Rows that should be excluded from selection when "Select All" is clicked */
  excludeWorkPacketIds: string[];
  /** Reset selection state */
  resetSelection: () => void;
  /** Callbacks for grid events */
  callbacks: {
    /** Triggered on any pagination change */
    onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
    /** Triggered on any filter change */
    onFilterModelChange: (filterModel: GridFilterModel) => void;
    /** Triggered on any sort change */
    onSortModelChange: (sortModel: GridSortModel) => void;
    /** Triggered when a row is updated */
    processRowUpdate: (updatedRow: any, originalRow: any) => any;
    /** Triggered when a row update fails */
    onProcessRowUpdateError: (error: any) => void;
    /** Triggered when row selection changes */
    onRowSelectionModelChange: (newSelection: GridRowSelectionModel) => void;
  };
  /** Text overrides for the grid */
  localeText: Partial<GridLocaleText>;

  /** Force trigger a fetch of work packets */
  triggerFetchWorkPackets: () => void;

  /** Call API to export work packets */
  handleExport: () => void;

  /** Export status */
  isExporting: boolean;

  /** Count of effectively selected rows considering select-all and exclusions */
  effectiveSelectionCount: number;
}

const myPacketOwnerViewFilterMap: Record<MyPacketOwnerView, (userId: string) => Partial<WorkPacketFilters>> = {
  [MyPacketOwnerView.CurrentPacketOwner]: userId => ({
    [WorkPacketFilter.CurrentPacketOwner]: userId,
    [WorkPacketFilter.BackupPacketOwner]: "",
    [WorkPacketFilter.MonitoringPacketOwner]: "",
  }),
  [MyPacketOwnerView.BackupPacketOwner]: userId => ({
    [WorkPacketFilter.CurrentPacketOwner]: "",
    [WorkPacketFilter.BackupPacketOwner]: userId,
    [WorkPacketFilter.MonitoringPacketOwner]: "",
  }),
  [MyPacketOwnerView.MonitoringPacketOwner]: userId => ({
    [WorkPacketFilter.CurrentPacketOwner]: "",
    [WorkPacketFilter.BackupPacketOwner]: "",
    [WorkPacketFilter.MonitoringPacketOwner]: userId,
  }),
};

const computeFilters = (filters: WorkPacketFilters, view: WorkPacketView, userId?: string) => {
  if (view === WorkPacketView.MyPackets) {
    if (!userId) {
      console.warn("User ID is required for My Packets view");
      return filters;
    }
    const myPacketOwnerView = filters[WorkPacketFilter.MyPacketsView];
    return {
      ...filters,
      ...myPacketOwnerViewFilterMap[myPacketOwnerView](userId),
    };
  }

  const currentPacketOwnerMap: Record<WorkPacketView, string> = {
    [WorkPacketView.MyPackets]: userId || "",
    [WorkPacketView.NewPackets]: "UNASSIGNED",
    [WorkPacketView.AllPackets]: filters[WorkPacketFilter.CurrentPacketOwner],
  };
  return {
    ...filters,
    /* Force current packet owner filter if the view is "My Packets" */
    [WorkPacketFilter.CurrentPacketOwner]: currentPacketOwnerMap[view],
  };
};

const getSortOption = (sortModel: GridSortModel): WorkPacketsAPI.SortOption | undefined => {
  if (!sortModel[0]) return;
  const { field: key, sort: direction } = sortModel[0];
  if (!direction) return;
  return { key, direction };
};

export interface FetchArgs {
  workPacketType: WorkPacketType;
  page: number;
  pageSize: number;
  filters: WorkPacketFilters;
  sorting?: WorkPacketsAPI.SortOption;
}

/**
 * Custom hook to manage fetching, filtering, sorting, and paginating work packets.
 */
export const useWorkPacketsGrid = <T extends WorkPacketType>(
  workPacketType: T,
  triggerSummaryRefetch?: () => void,
): UseWorkPacketsReturnType<T> => {
  type LocalWorkPacket = WorkPacketTypeMap[T];
  const { user } = useAuth0();

  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, _setSortModel] = useState<GridSortModel>([]);
  const setSortModel = useCallback(
    (sortModel: GridSortModel) => {
      _setSortModel(sortModel);
    },
    [_setSortModel],
  );

  const { currentView: view, currentFilters, setFilters } = useWorkPacketsContext();

  const computedFilters = useMemo(
    () => computeFilters(currentFilters, view, user?.sub),
    [view, currentFilters, user?.sub],
  );

  const [isUpdating, setIsUpdating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const queryClient = useQueryClient();

  const fetchArgs = useMemo<FetchArgs>(
    () => ({
      workPacketType,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      filters: computedFilters,
      sorting: getSortOption(sortModel),
    }),
    [computedFilters, paginationModel.page, paginationModel.pageSize, sortModel, workPacketType],
  );
  const query = useQuery({
    queryKey: ["work-packets", workPacketType, "list", fetchArgs] as const,
    queryFn: ({ queryKey: [, workPacketType, , fetchArgs], signal }) =>
      WorkPacketsAPI.fetchWorkPackets(
        {
          page: fetchArgs.page,
          pageSize: fetchArgs.pageSize,
          filters: fetchArgs.filters,
          sorting: fetchArgs.sorting,
          type: workPacketType,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const data = query.data?.workPackets || EMPTY_ARRAY;
  const total = query.data?.totalRecords ?? -1;
  const loading = query.isFetching || isUpdating;

  const triggerFetchWorkPackets = () => {
    triggerSummaryRefetch?.();

    return queryClient.invalidateQueries({
      queryKey: ["work-packets", workPacketType, "list"],
    });
  };

  const handlePaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
  }, []);

  const handleRowUpdate = async (updatedRow: LocalWorkPacket, originalRow: LocalWorkPacket) => {
    try {
      setIsUpdating(true);

      const updatedFields = getUpdatedFields(originalRow, updatedRow);
      if (Object.keys(updatedFields).length === 0) return originalRow;

      if (updatedRow.evaluatedActivationStatus && updatedRow.evaluatedActivationStatus.state !== "active") {
        const message =
          updatedRow.evaluatedActivationStatus.state === "sunset"
            ? "This work packet's scope is in the sunset state."
            : "This work packet's store is deactivated.";
        if (!confirm(`${message} Are you sure you want to update it?`)) {
          return originalRow;
        }
      }

      await WorkPacketsAPI.updateWorkPacket({
        workPacketType: workPacketType,
        workPacketId: updatedRow.packetId,
        updatedFields,
      });
      queryClient.invalidateQueries({
        queryKey: ["work-packets", updatedRow.workPacketType, "details", updatedRow.packetId],
      });

      triggerFetchWorkPackets();
      return updatedRow;
    } catch (error) {
      console.error("Error updating row:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRowError = (error: any) => {
    console.error("Error updating row", error);
  };

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    console.info("Grid Filter change", { filterModel });
  }, []);

  const handleSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setSortModel(sortModel);
    },
    [setSortModel],
  );

  const handleExport = useCallback(async () => {
    // Limiting export to 100k rows
    if (total >= 100000) {
      toast.error("Export row limit of 100k exceeded. Please apply some filters to the data set and try again.", {
        duration: 4000,
      });
      return;
    }

    if (!user || !user.sub) return;

    try {
      setIsExporting(true);
      await WorkPacketsAPI.exportWorkPackets({
        workPacketType: workPacketType,
        userId: user.sub,
        filters: computedFilters,
      });

      toast.success("Export completed!");
    } catch (error) {
      console.error("Error while exporting:", error);
      toast.error("Error exporting work packets");
    } finally {
      setIsExporting(false);
    }
  }, [user, computedFilters, total, workPacketType]);

  const {
    selectedIds,
    excludedIds,
    clearSelection,
    selectionMode,
    handleRowSelection,
    localeText,
    effectiveSelectionCount,
  } = useRowSelection(data, "packetId", total);

  // De-select all the rows when filters change
  useEffect(() => {
    clearSelection();
  }, [currentFilters, clearSelection]);

  return {
    data,
    paginationModel,
    filters: computedFilters,
    setFilters,
    total,
    loading,
    rowSelection: selectedIds,
    selectAllEnabled: selectionMode === SelectionMode.ExclusionBased,
    resetSelection: clearSelection,
    excludeWorkPacketIds: excludedIds,
    triggerFetchWorkPackets,
    handleExport,
    isExporting,
    callbacks: {
      onPaginationModelChange: handlePaginationModelChange,
      onFilterModelChange: onFilterChange,
      onSortModelChange: handleSortModelChange,
      processRowUpdate: handleRowUpdate,
      onProcessRowUpdateError: handleRowError,
      onRowSelectionModelChange: handleRowSelection,
    },
    localeText,
    effectiveSelectionCount,
  };
};
